import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, formatError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import MyPublicProfileCard from "@/components/MyPublicProfileCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, LogOut, Stethoscope, Activity, Wallet, Users, CalendarIcon, CheckCircle2, Volume2, VolumeX, AlertTriangle, Clock, UserX, Star } from "lucide-react";
import { toast } from "sonner";
import ChatPanel from "@/components/ChatPanel";
import ABDMIndex from "@/components/ABDMIndex";
import VoicePrescription from "@/components/VoicePrescription";
import ScheduleManager from "@/components/ScheduleManager";
import CancelOpdDialog from "@/components/CancelOpdDialog";
import PrescriptionForm from "@/components/PrescriptionForm";
import FollowupSetter from "@/components/FollowupSetter";
import OpdToIpdActions from "@/components/ipd/OpdToIpdActions";
import MyRatingsCard from "@/components/MyRatingsCard";
import { format } from "date-fns";
import { playAlertBeep, tryNotify, requestNotificationPermission } from "@/lib/alerts";

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date());
  const [history, setHistory] = useState([]);
  const [seenIds, setSeenIds] = useState(() => new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("upchaar_sound") !== "0";
  });
  const [firstLoad, setFirstLoad] = useState(true);
  const [scheduleOrgs, setScheduleOrgs] = useState([]);

  const load = async () => {
    const { data } = await api.get("/dashboard/doctor");
    setData(data);
  };

  const loadHistory = async (d) => {
    const ds = format(d, "yyyy-MM-dd");
    const { data } = await api.get(`/dashboard/doctor/history?date=${ds}`);
    setHistory(data);
  };

  const pollNotifications = async () => {
    try {
      const { data: notes } = await api.get("/notifications");
      const unread = notes.filter((n) => !n.read);
      setUnreadCount(unread.length);
      setSeenIds((prev) => {
        const next = new Set(prev);
        let newCount = 0;
        unread.forEach((n) => {
          if (!prev.has(n.id)) {
            // Only show toasts/sound for items that arrive AFTER initial load,
            // otherwise refreshing the page would spam old notifications.
            if (!firstLoad) {
              toast.message(n.title, { description: n.body });
              tryNotify(n.title, n.body);
              newCount += 1;
            }
            next.add(n.id);
          }
        });
        if (newCount > 0 && soundOn) playAlertBeep();
        return next;
      });
      if (firstLoad) setFirstLoad(false);
    } catch (_) { /* ignore */ }
  };

  const markNotificationsRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setUnreadCount(0);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    load();
    pollNotifications();
    requestNotificationPermission();
    api.get("/doctors/me/orgs").then((r) => setScheduleOrgs(r.data || [])).catch(() => {});
    const t = setInterval(() => { load(); pollNotifications(); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSound = () => {
    setSoundOn((s) => {
      const next = !s;
      localStorage.setItem("upchaar_sound", next ? "1" : "0");
      if (next) playAlertBeep();
      return next;
    });
  };

  useEffect(() => { loadHistory(date); }, [date]);

  const toggle = async (field, value) => {
    try {
      const { data } = await api.put("/doctors/me/status", { [field]: value });
      setUser({ ...user, ...data });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const startDiagnose = async (id) => {
    try {
      await api.post(`/patients/${id}/diagnose`);
      toast.success("Now checking patient");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const checkoutComplete = async (id) => {
    try {
      await api.post(`/patients/${id}/checkout`);
      toast.success("Checkout complete");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!user || !data) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="doctor-dashboard-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-3 sm:px-6 py-3 flex items-center justify-between gap-2 max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
              {user.profile_pic ? <img src={fileUrl(user.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                <span className="font-semibold text-slate-600">{user.name?.[0]}</span>}
            </div>
            <div className="min-w-0">
              <div className="font-heading text-base font-semibold text-slate-900 truncate" data-testid="doctor-name">{user.name}</div>
              <div className="text-xs text-slate-500 truncate">{user.specialization_en} · CID: <span className="font-mono">{user.cid}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <ChatPanel />
            <Button variant="outline" size="icon" onClick={toggleSound} title={soundOn ? "Mute alerts" : "Unmute alerts"} data-testid="sound-toggle-btn">
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-slate-400" />}
            </Button>
            <Button variant="outline" size="icon" className="relative" onClick={markNotificationsRead} data-testid="notification-bell-doctor">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 text-xs rounded-full bg-emerald-500 text-white flex items-center justify-center" data-testid="notification-badge">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="doctor-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-3 sm:px-6 py-4 sm:py-8 max-w-[1400px] mx-auto space-y-6 sm:space-y-8 pb-24">
        {/* Status toggles */}
        <section className="grid sm:grid-cols-2 gap-4">
          <Card className="medical-card p-5 flex items-center justify-between" data-testid="toggle-cabin-card">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Cabin Status</p>
              <p className="font-heading text-xl font-semibold mt-1 text-slate-900">{user.in_cabin ? "In Cabin" : "Out of Cabin"}</p>
            </div>
            <Switch checked={!!user.in_cabin} onCheckedChange={(v) => toggle("in_cabin", v)} data-testid="toggle-cabin" />
          </Card>
          <Card className="medical-card p-5 flex items-center justify-between" data-testid="toggle-active-card">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Availability</p>
              <p className="font-heading text-xl font-semibold mt-1 text-slate-900">{user.availability === "active" ? "Active" : "On Leave"}</p>
            </div>
            <Switch
              checked={user.availability === "active"}
              onCheckedChange={(v) => toggle("availability", v ? "active" : "on_leave")}
              data-testid="toggle-active"
            />
          </Card>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="medical-card p-5" data-testid="doc-stat-today">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Today Earning</p>
              <Wallet className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="font-heading text-3xl font-bold mt-2 text-emerald-600">₹{data.today_earning}</p>
          </Card>
          <Card className="medical-card p-5" data-testid="doc-stat-month">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">This Month</p>
              <Activity className="w-4 h-4 text-sky-500" />
            </div>
            <p className="font-heading text-3xl font-bold mt-2 text-sky-600">₹{data.month_earning}</p>
          </Card>
          <Card className="medical-card p-5" data-testid="doc-stat-rating">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">My Rating</p>
              <Star className="w-4 h-4 text-amber-500" />
            </div>
            <p className="font-heading text-3xl font-bold mt-2 text-amber-600">
              {data.my_rating > 0 ? data.my_rating.toFixed(1) : "—"}
              <span className="text-sm text-slate-400 font-normal ml-1">/ 5</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {data.my_rating_count > 0 ? `${data.my_rating_count} review${data.my_rating_count !== 1 ? "s" : ""}` : "No reviews yet"}
            </p>
          </Card>
          <Card className="medical-card p-5" data-testid="doc-stat-total-patients">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Total Patients</p>
              <Users className="w-4 h-4 text-slate-500" />
            </div>
            <p className="font-heading text-3xl font-bold mt-2 text-slate-900">{data.total_patients_count}</p>
          </Card>
        </section>

        {/* Shareable public profile */}
        <MyPublicProfileCard />

        {/* Current Patient */}
        <section>
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Current Checking Patient</h2>
          <Card className="medical-card p-5 sm:p-6" data-testid="current-patient-card">
            {data.current ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-4 sm:gap-6 flex-1 min-w-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
                    {data.current.photo ? <img src={fileUrl(data.current.photo)} alt="" className="w-full h-full object-cover" /> :
                      <span className="font-semibold text-2xl text-slate-600">{data.current.name?.[0]}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">#{data.current.ticket_number}</Badge>
                      <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 truncate">{data.current.name}</h3>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Age {data.current.age} · {data.current.mobile}</p>
                  </div>
                </div>
                <Button onClick={() => checkoutComplete(data.current.id)} className="hidden sm:inline-flex bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="checkout-complete-btn">
                  <CheckCircle2 className="w-4 h-4" /> Checkout Complete
                </Button>
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No patient currently being checked. Tap &quot;Diagnose&quot; below to start.</p>
            )}
            {data.current && (
              <>
                <VoicePrescription patientId={data.current.id} initial={data.current.prescription || ""} />
                <div className="mt-3 flex flex-wrap gap-2">
                  <PrescriptionForm
                    api={api}
                    patient={data.current}
                    onSaved={() => load()}
                  />
                  <FollowupSetter
                    api={api}
                    patient={data.current}
                    onSaved={() => load()}
                  />
                  <OpdToIpdActions patient={data.current} />
                </div>
              </>
            )}
          </Card>
        </section>

        {/* Delay & Bypass controls */}
        <section className="grid sm:grid-cols-3 gap-3">
          <Button variant="outline" onClick={async () => {
            const m = parseInt(window.prompt("Delay all waiting patients by how many minutes?", "30") || "0");
            if (m > 0) {
              try { const r = await api.post("/doctors/me/delay", { minutes: m }); toast.success(`Notified ${r.data.notified} patients`); }
              catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }
          }} className="gap-2 h-12" data-testid="doctor-delay-btn">
            <Clock className="w-4 h-4" /> Announce Delay
          </Button>
          <Button variant="outline" onClick={() => toast.message("Tap the Emergency button on any waiting patient row to bypass.")} className="gap-2 h-12 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="doctor-emergency-help-btn">
            <AlertTriangle className="w-4 h-4" /> Emergency Bypass · how
          </Button>
          <ScheduleManager api={api} orgs={scheduleOrgs} />
        </section>

        {/* Emergency Leave / Cancel OPD — full-width, high-contrast CTA */}
        <section>
          <div className="rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 to-white p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="cancel-opd-banner">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-rose-600 font-bold">Emergency leave</div>
              <p className="text-sm text-slate-700 mt-1">
                One-tap mass cancel for today&apos;s OPD. Every active booking is cancelled, paid tickets are auto-refunded, and SMS + WhatsApp go out to every patient.
              </p>
            </div>
            <CancelOpdDialog api={api} onCancelled={() => load()} />
          </div>
        </section>

        {/* Patient queue */}
        <section>
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Upcoming Queue</h2>

          {/* Mobile queue cards */}
          <div className="sm:hidden space-y-3">
            {data.queue.length === 0 ? (
              <Card className="medical-card p-6 text-center text-slate-500 text-sm" data-testid="empty-queue-mobile">Queue is clear.</Card>
            ) : data.queue.map((p) => (
              <Card key={p.id} className="medical-card p-4 flex items-center gap-3" data-testid={`queue-card-${p.id}`}>
                <Badge variant="outline" className="font-mono shrink-0">#{p.ticket_number}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">Age {p.age} · {p.mobile}</div>
                </div>
                <Button onClick={() => startDiagnose(p.id)} disabled={!!data.current} size="sm" className="bg-sky-500 hover:bg-sky-600 text-white shrink-0" data-testid={`diagnose-mobile-btn-${p.id}`}>
                  <Stethoscope className="w-3.5 h-3.5" />
                </Button>
              </Card>
            ))}
          </div>

          {/* Desktop queue table */}
          <Card className="medical-card overflow-hidden hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Ticket</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.queue.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10" data-testid="empty-queue">Queue is clear.</TableCell></TableRow>
                ) : data.queue.map((p) => (
                  <TableRow key={p.id} data-testid={`queue-row-${p.id}`}>
                    <TableCell><Badge variant="outline" className="font-mono">#{p.ticket_number}</Badge></TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.age}</TableCell>
                    <TableCell>{p.mobile}</TableCell>
                    <TableCell className="text-right">
                      <Button onClick={() => startDiagnose(p.id)} disabled={!!data.current} size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid={`diagnose-btn-${p.id}`}>
                        <Stethoscope className="w-3.5 h-3.5 mr-1.5" /> Diagnose
                      </Button>
                      <Button onClick={async () => {
                        try { const r = await api.post(`/patients/${p.id}/emergency-bypass`); toast.success(`Moved to front · ${r.data.shifted_waiting} notified`); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} size="sm" variant="outline" className="ml-2 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`emergency-btn-${p.id}`}>
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </Button>
                      <Button onClick={async () => {
                        try {
                          const r = await api.post(`/patients/${p.id}/no-show`);
                          if (r.data.action === "cancelled") toast.message(`Cancelled · 3 strikes`, { description: `${p.name} was cancelled after 3 no-shows.` });
                          else toast.message(`Pushed back ${r.data.pushback}`, { description: `${p.name} moved back. Strike ${r.data.strikes}/3.` });
                          load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} size="sm" variant="outline" className="ml-2 border-slate-300 text-slate-600 hover:bg-slate-50" data-testid={`no-show-btn-${p.id}`}>
                        <UserX className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>

        {/* Report Data */}
        <section>
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Report Data</h2>
          <Card className="medical-card p-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="history-date-btn">
                    <CalendarIcon className="w-4 h-4" /> {format(date, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} />
                </PopoverContent>
              </Popover>
              <Badge variant="secondary" className="text-sm">Checked: {history.filter(h => h.status === "completed").length}</Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No records for this date</TableCell></TableRow>
                ) : history.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono">#{p.ticket_number}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.age}</TableCell>
                    <TableCell>{p.mobile}</TableCell>
                    <TableCell><Badge variant={p.status === "completed" ? "default" : "outline"}>{p.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>

        {/* My ratings */}
        <section>
          <MyRatingsCard api={api} />
        </section>
        {/* ABDM Integration */}
        <section>
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">🏥 ABDM - Patient Records</h2>
          <Card className="medical-card">
            <ABDMIndex />
          </Card>
        </section>
      </main>

      {/* Sticky bottom action bar — mobile only */}
      {data.current && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-white border-t border-slate-200 p-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]" data-testid="mobile-action-bar">
          <Button onClick={() => checkoutComplete(data.current.id)} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white gap-2 h-12 text-base" data-testid="checkout-complete-mobile-btn">
            <CheckCircle2 className="w-5 h-5" /> Checkout Complete · #{data.current.ticket_number}
          </Button>
        </div>
      )}
    </div>
  );
}
